import { Button, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useJoinInfo, useRegistration } from '~/api/hooks'
import { ErasureLines } from '~/components/member/ErasureLines'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { MemberLayout, PageTitle } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { useHashParams } from '~/lib/hash'
import { forgetErasedPerson } from '~/lib/session'

/**
 * `/join/delete#t=…` (before a password is chosen). The token stays in the fragment and is posted only when the
 * button is pressed (R23). The lines about the company that did the check come from the registration's provider
 * (`erasureNotice`) when the browser still has the registration cookie and a check was started.
 */
export default function DeleteRegistration() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const params = useHashParams()
  const token = params.get('t') ?? params.get('token')
  const reg = useRegistration()
  const join = useJoinInfo(reg.data?.provider)
  const hadCheck = Boolean(reg.data?.attempt)
  const erase = useMutation({
    mutationFn: () => api('memberEraseLink', { body: { token: token! } }),
    // The registration is gone: so are the name, email and place this browser kept for it.
    onSuccess: () => forgetErasedPerson(qc),
  })
  const oldLink = !token || (erase.isError && isApiError(erase.error, 'invalid_link'))

  return (
    <MemberLayout>
      <PageTitle title={t('deleteReg.title')} />
      {erase.isSuccess ? (
        <>
          <Text role='status'>{t(hadCheck ? 'deleteReg.done' : 'deleteReg.doneNoCheck')}</Text>
          <ActionBar>
            <Button asChild size='lg' w='full'>
              <RouterLink to='/join'>{t('deleteReg.again')}</RouterLink>
            </Button>
          </ActionBar>
        </>
      ) : oldLink ? (
        <Text role='alert'>{t('deleteReg.oldLink')}</Text>
      ) : (
        <>
          <Text>{t('deleteReg.text')}</Text>
          <ErasureLines
            notice={hadCheck ? join.data?.erasureNotice : undefined}
            provider={hadCheck ? reg.data?.providerDisplayName : undefined}
          />
          {erase.isError && <ErrorAlert>{friendly(erase.error)}</ErrorAlert>}
          <ActionBar>
            <Button
              size='lg'
              w='full'
              colorPalette='red'
              bg='red.700'
              _hover={{ bg: 'red.800' }}
              color='white'
              loading={erase.isPending}
              onClick={() => erase.mutate()}
            >
              {t('deleteReg.button')}
            </Button>
          </ActionBar>
        </>
      )}
    </MemberLayout>
  )
}
